import React, { useEffect, useState } from 'react';
// Phase 7 sign-off: profiles email, password, long_text.
import { MAX_LENGTHS, useCappedPaste } from '../utils/inputValidation';
import { useTranslation } from 'react-i18next';

/**
 * Confirmation modal for destructive offboarding actions.
 * mode = 'deactivate' | 'delete'
 *  - deactivate: requires typing email to confirm
 *  - delete:     requires typing email AND entering current password
 *
 * onConfirm({ reason, password }) — password is empty string in deactivate mode.
 */
export default function ConfirmOffboardModal({
  open,
  mode = 'deactivate',
  userEmail = '',
  busy = false,
  onCancel,
  onConfirm,
}) {
  const { i18n } = useTranslation();
  const isKo = i18n.language === 'ko';
  const [typedEmail, setTypedEmail] = useState('');
  const [password, setPassword] = useState('');
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (open) {
      setTypedEmail('');
      setPassword('');
      setReason('');
    }
  }, [open, mode]);

  // Hooks must be called before any early return (Rules of Hooks).
  const onEmailPaste    = useCappedPaste(MAX_LENGTHS.email);
  const onPasswordPaste = useCappedPaste(MAX_LENGTHS.password);
  const onReasonPaste   = useCappedPaste(MAX_LENGTHS.long_text);

  if (!open) return null;

  const isDelete = mode === 'delete';
  const emailMatches =
    !!userEmail && typedEmail.trim().toLowerCase() === userEmail.trim().toLowerCase();
  const canSubmit = emailMatches && (!isDelete || password.length > 0) && !busy;

  const titleKo = isDelete ? '계정 영구 삭제 확인' : '계정 비활성화 확인';
  const titleEn = isDelete ? 'Confirm permanent deletion' : 'Confirm account deactivation';

  const bodyKo = isDelete
    ? '이 작업은 즉시 적용되며 되돌릴 수 없습니다. 모든 개인정보가 비식별화됩니다.'
    : '계정이 비활성화되며 30일 이내에 다시 로그인하면 복구할 수 있습니다. 30일이 지나면 영구 삭제됩니다.';
  const bodyEn = isDelete
    ? 'This is immediate and cannot be undone. All personal data will be anonymized.'
    : 'Your account will be deactivated. You can restore it by signing back in within 30 days; after that, it is permanently deleted.';

  const submit = (e) => {
    e?.preventDefault?.();
    if (!canSubmit) return;
    onConfirm?.({ reason, password: isDelete ? password : '' });
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000, padding: 16,
      }}
    >
      <form
        onSubmit={submit}
        className="rounded-lg p-6 w-full max-w-md space-y-4"
        style={{ background: 'var(--surface-primary, #fff)', color: 'var(--text-primary, #111)' }}
      >
        <h3 className="text-lg font-bold" style={{ color: isDelete ? '#991B1B' : '#92400E' }}>
          {isKo ? titleKo : titleEn}
        </h3>
        <p className="text-sm" style={{ color: 'var(--text-secondary, #555)' }}>
          {isKo ? bodyKo : bodyEn}
        </p>

        <label className="block text-sm">
          <span className="block mb-1">
            {isKo
              ? <>확인을 위해 본인 이메일 <strong>{userEmail}</strong>을(를) 입력하세요.</>
              : <>Type your email <strong>{userEmail}</strong> to confirm.</>}
          </span>
          <input
            type="email"
            value={typedEmail}
            onChange={(e) => setTypedEmail(e.target.value)}
            onPaste={onEmailPaste}
            maxLength={MAX_LENGTHS.email}
            className="w-full rounded-md border px-3 py-2 text-sm"
            style={{ borderColor: 'var(--border-color, #ccc)' }}
            autoComplete="off"
            autoFocus
          />
        </label>

        {isDelete && (
          <label className="block text-sm">
            <span className="block mb-1">
              {isKo ? '현재 비밀번호' : 'Current password'}
            </span>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onPaste={onPasswordPaste}
              maxLength={MAX_LENGTHS.password}
              className="w-full rounded-md border px-3 py-2 text-sm"
              style={{ borderColor: 'var(--border-color, #ccc)' }}
              autoComplete="current-password"
            />
          </label>
        )}

        <label className="block text-sm">
          <span className="block mb-1" style={{ color: 'var(--text-secondary, #555)' }}>
            {isKo ? '사유 (선택)' : 'Reason (optional)'}
          </span>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            onPaste={onReasonPaste}
            rows={2}
            maxLength={MAX_LENGTHS.long_text}
            className="w-full rounded-md border px-3 py-2 text-sm"
            style={{ borderColor: 'var(--border-color, #ccc)' }}
          />
        </label>

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="px-4 py-2 rounded-md text-sm"
            style={{ background: 'transparent', color: 'var(--text-secondary, #555)', border: '1px solid var(--border-color, #ccc)' }}
          >
            {isKo ? '취소' : 'Cancel'}
          </button>
          <button
            type="submit"
            disabled={!canSubmit}
            className="px-4 py-2 rounded-md text-sm font-medium"
            style={{
              background: canSubmit ? (isDelete ? '#DC2626' : '#F59E0B') : '#9CA3AF',
              color: '#fff',
              cursor: canSubmit ? 'pointer' : 'not-allowed',
            }}
          >
            {busy
              ? (isKo ? '처리 중…' : 'Working…')
              : isDelete
                ? (isKo ? '영구 삭제' : 'Delete permanently')
                : (isKo ? '비활성화' : 'Deactivate')}
          </button>
        </div>
      </form>
    </div>
  );
}
